import { formatEuro } from './money.js';

/**
 * Preisregeln je Basar (Einstellungs-Audit, Paket B). Eine Quelle für Server und Web: Der Server erzwingt, die Oberfläche
 * zeigt dieselbe Meldung vorab als Hilfe. Preise sind brutto in ganzen Cent (Regel 3).
 *
 * - Höchstpreis ist fest (5.000 €), nicht einstellbar.
 * - Mindestpreis je Artikel: Standard 0,50 € (die Untergrenze von Kasse und Aktionspreis, nicht niedriger einstellbar), höchstens 100 €.
 * - Preisschritt: Standard 1 Cent (jeder Cent-Betrag, wie bisher), sonst 10 Cent, 50 Cent oder 1 €.
 * Geprüft werden nur neue oder geänderte Preise, bestehende Artikel bleiben gültig.
 */

/** Höchstpreis je Artikel in Cent (fest). */
export const ITEM_MAX_PRICE_CENTS = 500_000;
/** Standard und Untergrenze des Mindestpreises in Cent (heutiges Verhalten). */
export const ITEM_MIN_PRICE_DEFAULT_CENTS = 50;
/** Obergrenze, auf die sich der Mindestpreis einstellen lässt (100 €). */
export const ITEM_MIN_PRICE_SETTING_MAX_CENTS = 10_000;

/** Wählbare Preisschritte in Cent; der erste ist der Standard (jeder Cent-Betrag). */
export const PRICE_STEP_OPTIONS = [1, 10, 50, 100] as const;
export type PriceStepCents = (typeof PRICE_STEP_OPTIONS)[number];
export const PRICE_STEP_DEFAULT_CENTS: PriceStepCents = 1;

export function isPriceStepCents(value: number): value is PriceStepCents {
  return (PRICE_STEP_OPTIONS as readonly number[]).includes(value);
}

/** Beschriftung eines Preisschritts für die Oberfläche, z. B. „10 Cent“, „1 €“. */
export function priceStepLabel(step: number): string {
  if (step === 1) return '1 Cent (jeder Betrag)';
  return step >= 100 && step % 100 === 0 ? `${String(step / 100)} €` : `${String(step)} Cent`;
}

/** Ist `cents` ein zulässiger Wert für die Einstellung „Mindestpreis“? */
export function isMinPriceSetting(cents: number): boolean {
  return (
    Number.isSafeInteger(cents) &&
    cents >= ITEM_MIN_PRICE_DEFAULT_CENTS &&
    cents <= ITEM_MIN_PRICE_SETTING_MAX_CENTS
  );
}

export interface PriceRules {
  minPriceCents: number;
  priceStepCents: number;
}

export const DEFAULT_PRICE_RULES: PriceRules = {
  minPriceCents: ITEM_MIN_PRICE_DEFAULT_CENTS,
  priceStepCents: PRICE_STEP_DEFAULT_CENTS,
};

/** Meldung für einen Preis außerhalb von Mindest- und Höchstpreis (bei Standardregeln der bisherige Wortlaut). */
export function priceRangeMessage(minPriceCents: number): string {
  return `Der Preis muss zwischen ${shortEuro(minPriceCents)} und ${shortEuro(ITEM_MAX_PRICE_CENTS)} liegen.`;
}

/** Kurze Schreibweise mit gewöhnlichem Leerzeichen, wie in den bisherigen Meldungen: „0,50 €“, „5.000 €“. */
function shortEuro(cents: number): string {
  return formatEuro(cents).replace(',00', '').replace('\u00a0', ' ');
}

/**
 * Prüft einen Preis gegen die Regeln des Basars. `null` = in Ordnung, sonst eine verständliche deutsche Meldung.
 * Reihenfolge: ganze Cent, Bereich (Mindest- und Höchstpreis), Schritt.
 */
export function priceRuleError(priceCents: number, rules: PriceRules): string | null {
  if (
    !Number.isSafeInteger(priceCents) ||
    priceCents < rules.minPriceCents ||
    priceCents > ITEM_MAX_PRICE_CENTS
  ) {
    return priceRangeMessage(rules.minPriceCents);
  }
  const step = rules.priceStepCents;
  if (step > 1 && priceCents % step !== 0) {
    const lower = priceCents - (priceCents % step);
    const upper = lower + step;
    const suggestions = [lower, upper].filter(
      (cents) => cents >= rules.minPriceCents && cents <= ITEM_MAX_PRICE_CENTS,
    );
    const hint =
      suggestions.length === 0
        ? ''
        : ` Zum Beispiel ${suggestions.map((cents) => shortEuro(cents)).join(' oder ')}.`;
    return `Der Preis muss in Schritten von ${shortEuro(step)} angegeben werden.${hint}`;
  }
  return null;
}

/** Liest Preisregeln aus Datenbankwerten (Zeichenketten oder Zahlen). Ungültige oder fehlende Werte ergeben den Standard. */
export function priceRulesOf(minPriceCents: unknown, priceStepCents: unknown): PriceRules {
  const min = Number(minPriceCents);
  const step = Number(priceStepCents);
  return {
    minPriceCents: isMinPriceSetting(min) ? min : DEFAULT_PRICE_RULES.minPriceCents,
    priceStepCents: isPriceStepCents(step) ? step : DEFAULT_PRICE_RULES.priceStepCents,
  };
}

/** Hinweis unter dem Preisfeld: bei Standardregeln der bisherige Text, sonst mit Mindestpreis und Schritt des Basars. */
export function priceRuleHint(rules: PriceRules): string {
  const isDefault =
    rules.minPriceCents === ITEM_MIN_PRICE_DEFAULT_CENTS &&
    rules.priceStepCents === PRICE_STEP_DEFAULT_CENTS;
  if (isDefault) return 'Endpreis, z. B. 8 oder 8,50';
  const parts: string[] = [];
  if (rules.minPriceCents !== ITEM_MIN_PRICE_DEFAULT_CENTS) {
    parts.push(`mindestens ${shortEuro(rules.minPriceCents)}`);
  }
  if (rules.priceStepCents > PRICE_STEP_DEFAULT_CENTS) {
    parts.push(`in Schritten von ${shortEuro(rules.priceStepCents)}`);
  }
  return `Endpreis, ${parts.join(' und ')}`;
}

/**
 * Prüft die Kombination der beiden Einstellungen: Der Mindestpreis muss ein Vielfaches des Preisschritts sein, sonst wäre
 * der Mindestpreis selbst kein erlaubter Preis. `null` = in Ordnung, sonst eine verständliche Meldung.
 */
export function priceRulesCombinationError(
  minPriceCents: number,
  priceStepCents: number,
): string | null {
  if (priceStepCents <= 1 || minPriceCents % priceStepCents === 0) return null;
  const next = Math.ceil(minPriceCents / priceStepCents) * priceStepCents;
  return `Der Mindestpreis ${shortEuro(minPriceCents)} passt nicht zum Preisschritt ${shortEuro(priceStepCents)}: Er muss ein Vielfaches davon sein, z. B. ${shortEuro(next)}.`;
}
